import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

@component({ name: "pill-select" })
export class PillSelect<T extends string> extends LonaWebComponent {
  private $container: HTMLElement = this.$("container");

  private selectedValue: Option<T> = null;
  private entries: PillSelect.Item<T>[] = [];
  onSelected?: (value: T) => void;

  bind(
    selected: T,
    entries: PillSelect.Item<T>[],
  ): this {
    this.selectedValue = selected;
    this.entries = entries;
    this.render();
    return this;
  }

  setSelected(value: T, notify: boolean = false): void {
    if (this.selectedValue === value) return;
    this.selectedValue = value;
    this.updateSelectedState();
    if (notify) {
      this.onSelected?.(value);
    }
  }

  private render(): void {
    DomUtils.clearChildren(this.$container);

    for (const entry of this.entries) {
      const $pill = document.createElement("button");
      $pill.className = "pill";
      $pill.setAttribute("data-value", entry.identifier);
      $pill.textContent = entry.label;

      if (entry.identifier === this.selectedValue) {
        $pill.classList.add("selected");
      }

      GESTURE_MANAGER.addPointerEvent($pill, {
        onClick: () => {
          this.selectedValue = entry.identifier;
          this.updateSelectedState();
          this.onSelected?.(entry.identifier);
        },
      });

      this.$container.appendChild($pill);
    }
  }

  private updateSelectedState(): void {
    for (const $pill of this.$container.querySelectorAll(".pill")) {
      const value = $pill.getAttribute("data-value");
      $pill.classList.toggle("selected", value === this.selectedValue);
    }
  }

  static $styles = [
    css`
      :host {
        display: block;
      }

      #container {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .pill {
        border: none;
        background: var(--bg-surface-light);
        color: var(--text-sec);
        padding: 6px 12px;
        border-radius: 16px;
        cursor: pointer;
        font-family: inherit;
        font-size: var(--text-13px);
        transition: background-color 0.15s ease, color 0.15s ease;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 200px;
      }

      .pill:hover {
        background: var(--divider);
      }

      .pill.selected {
        background: var(--blue);
        color: white;
      }

      .pill.selected:hover {
        background: var(--blue);
      }
    `,
  ];

  static $html = html`<div id="container"></div>`;
}

export namespace PillSelect {
  export type Item<T extends string> = {
    label: string;
    identifier: T;
  };
}
